import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { I18nProvider } from "@/contexts/I18nContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

import SiteHome from "@/pages/site/SiteHome";
import SiteAbout from "@/pages/site/SiteAbout";
import SiteGadgets from "@/pages/site/SiteGadgets";
import AutodiagnosticoSeisAreas from "@/pages/site/AutodiagnosticoSeisAreas";
import SiteProductos from "@/pages/site/SiteProductos";
import SiteObservatorio from "@/pages/site/SiteObservatorio";
import SiteMaestrasSombra from "@/pages/site/SiteMaestrasSombra";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AppShell from "@/pages/app/AppShell";
import AppHome from "@/pages/app/AppHome";
import ModuleStub from "@/pages/app/ModuleStub";

import CirculoRouter from "@/pages/app/circulo/CirculoRouter";
import Expediente from "@/pages/app/circulo/Expediente";
import NuevoCaso from "@/pages/app/circulo/NuevoCaso";
import AcceptInvite from "@/pages/app/circulo/AcceptInvite";

import AulaHome from "@/pages/app/aula/AulaHome";
import CourseView from "@/pages/app/aula/CourseView";
import NuevoCurso from "@/pages/app/aula/NuevoCurso";

import SintoniaHome from "@/pages/app/sintonia/SintoniaHome";
import PacienteView from "@/pages/app/sintonia/PacienteView";
import PublicBooking from "@/pages/site/PublicBooking";
import BrujulaHome from "@/pages/app/brujula/BrujulaHome";
import SelloRouter from "@/pages/app/sello/SelloRouter";
import VerifySello from "@/pages/site/VerifySello";
import SelloBadge from "@/pages/site/SelloBadge";
import Billing from "@/pages/app/billing/Billing";
import Checkout from "@/pages/app/checkout/Checkout";
import CheckoutSuccess from "@/pages/app/checkout/CheckoutSuccess";
import FamilySetup from "@/pages/app/checkout/FamilySetup";
import Actividad from "@/pages/app/admin/Actividad";
import Tablero from "@/pages/app/admin/Tablero";
import Boletin from "@/pages/app/admin/Boletin";
import Evidencia from "@/pages/app/admin/Evidencia";
import Historial from "@/pages/app/admin/Historial";
import Integraciones from "@/pages/app/admin/Integraciones";
import Newsletter from "@/pages/app/admin/Newsletter";
import Usuarios from "@/pages/app/admin/Usuarios";
import RequireModule from "@/components/RequireModule";
import PWAManager from "@/components/PWAManager";

import "@/App.css";

function ExpedienteByRole() {
  const { user } = useAuth();
  if (!user) return null;
  // Only the platform admin (Mariela = is_admin=true) gets the admin view.
  // Having role=psicologa alone is NOT enough — see Chiessa's identity/access rule.
  const viewMode = user.is_admin ? "admin"
    : user.role === "familia" ? "familia"
    : "docente";
  return <Expediente viewMode={viewMode} />;
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <PWAManager />
          <Routes>
            <Route path="/" element={<SiteHome />} />
            <Route path="/nosotros" element={<SiteAbout />} />
            <Route path="/psicogadgets" element={<SiteGadgets />} />
            <Route path="/autodiagnostico-seis-areas" element={<AutodiagnosticoSeisAreas />} />
            <Route path="/autodiagnostico" element={<AutodiagnosticoSeisAreas />} />
            <Route path="/productos" element={<SiteProductos />} />
            <Route path="/observatorio" element={<SiteObservatorio />} />
            <Route path="/maestras-sombra" element={<SiteMaestrasSombra />} />
            <Route path="/agendar/:slug" element={<PublicBooking />} />
            <Route path="/verificar/:code" element={<VerifySello />} />
            <Route path="/embed/sello/:code" element={<SelloBadge />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            {/* Public invite landing — MUST be outside AppShell so unauthenticated
                invitees can be redirected to login/register with prefilled email. */}
            <Route path="/app/invite/:token" element={<AcceptInvite />} />
            <Route path="/app" element={<AppShell />}>
              <Route index element={<AppHome />} />
              <Route path="circulo" element={<RequireModule module="circulo"><CirculoRouter /></RequireModule>} />
              <Route path="circulo/nuevo" element={<RequireModule module="circulo"><NuevoCaso /></RequireModule>} />
              {/* child detail: no RequireModule guard — a circle seat (child_access) is
                  enough to open ONE child's file regardless of the user's platform role.
                  Backend enforces access via _user_can_access_child. */}
              <Route path="circulo/child/:childId" element={<ExpedienteByRole />} />
              <Route path="aula" element={<RequireModule module="aula"><AulaHome /></RequireModule>} />
              <Route path="aula/nuevo" element={<RequireModule module="aula"><NuevoCurso /></RequireModule>} />
              <Route path="aula/edit/:courseId" element={<RequireModule module="aula"><NuevoCurso /></RequireModule>} />
              <Route path="aula/:courseId" element={<RequireModule module="aula"><CourseView /></RequireModule>} />
              <Route path="sintonia" element={<RequireModule module="sintonia"><SintoniaHome /></RequireModule>} />
              <Route path="sintonia/paciente/:patientId" element={<RequireModule module="sintonia"><PacienteView /></RequireModule>} />
              <Route path="brujula" element={<RequireModule module="brujula"><BrujulaHome /></RequireModule>} />
              <Route path="sello" element={<RequireModule module="sello"><SelloRouter /></RequireModule>} />
              <Route path="billing" element={<Billing />} />
              <Route path="tablero" element={<RequireModule module="tablero"><Tablero /></RequireModule>} />
              <Route path="boletin" element={<RequireModule module="boletin"><Boletin /></RequireModule>} />
              <Route path="evidencia" element={<RequireModule module="evidencia"><Evidencia /></RequireModule>} />
              <Route path="historial" element={<RequireModule module="historial"><Historial /></RequireModule>} />
              <Route path="admin/integraciones" element={<RequireModule module="tablero"><Integraciones /></RequireModule>} />
              <Route path="admin/newsletter" element={<RequireModule module="tablero"><Newsletter /></RequireModule>} />
              <Route path="checkout/:kind/:id" element={<Checkout />} />
              <Route path="checkout/success" element={<CheckoutSuccess />} />
              <Route path="checkout/family-setup/:txId" element={<FamilySetup />} />
              <Route path="admin/actividad" element={<RequireModule module="tablero"><Actividad /></RequireModule>} />
              <Route path="admin/usuarios" element={<RequireModule module="tablero"><Usuarios /></RequireModule>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
